/**
 * The package builder's adapter — `builder-engine-and-consistency.md` §5.2,
 * §7 (step 10 of `build-both-together.md`).
 *
 * What the package page is, against the three things a page may differ in:
 * its **money** is the server's `/preview` at a sample measure (never worked
 * out here), its visits **start relative** to the package, and its **quantity
 * cell is a rule**. Everything else is the quote's engine, and to keep it that
 * way this file does one thing the quote's adapter does not need to: it turns
 * the draft and the preview into the quote's own view shapes
 * (`QuoteVisitView`, `QuoteServiceView`, `QuoteLineView`), so `VisitCard`,
 * `ServiceRow`, `LineRow`, `PlansGrid` and every popover draw a package
 * without a branch that asks which page they are on.
 *
 * The draft is the one piece of state. Edits change it here, a preview of it
 * is asked for a quarter-second after the typing stops, and Save writes it
 * through the same `PUT` it always used. Ids in the views are positions —
 * `v0`, `v0.s1`, `v0.s1.l2` — the same keys the preview answers with.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  DEFAULT_QUESTION_NAME, NO_PROPOSAL_OVERRIDES, blankPackageBillingPlan, defaultPlanName,
  packageTimingOfWhen, packageWhenOfOldTiming,
  NO_SERVICE_NOTES, noteOverride, packageServiceNotes,
  resolveVisitDates, visitWhenOfPackage, visitWhenPhrase,
  type BillingPlanKind, type DaysRule, type LineHandling, type MilestoneTrigger, type PackageBillingPlanSpec, type VisitForDates, type VisitWhen, type MarginScope, type MeResponse, type PackageLineKind,
  type PackageLineSpec, type PackagePlanSpec, type PackagePreviewLine, type PackagePreviewResponse,
  type PackageServiceSpec, type PackageSpec, type PackageVisitSpec, type QuantityBand,
  type QuoteBillingPlanView, type QuoteLineKind, type QuoteLineView, type QuoteServiceView,
  type QuoteTree, type QuoteVisitView, type ScalesBy,
} from '@rowted/shared'
import { request } from '../../lib/api.js'
import { usePhone, useNarrow } from '../../lib/use-narrow.js'
import type { MenuItem } from '../../components/context-menu.js'
import type { Pickable } from '../../quote/quote-editors.js'
import type { Division, MeasurementType } from '../../lib/catalog-types.js'
import { OPTIONAL_LABEL, OPTIONAL_SUB, PLAN_VISIT_OPTIONAL, ROW_MENU, VISIT_MENU } from '../copy.js'
import type { BuilderAdapter } from '../model.js'
import { formatNumber } from '../../lib/format.js'
import type { RecipeLineDraft } from '../popovers/line-popover.js'
import { bandsOfDraft, draftTiersOf, readNumber } from '../quantity/tiers.js'

/** What the package builder is, against the things a page may differ in (§5.2). */
export const PACKAGE_ADAPTER: BuilderAdapter = {
  qty: 'rule',
  money: 'preview',
  starts: 'relative',
  toolbar: ['search', 'addVisit', 'addPackage', 'pricesAndCosts', 'collapseAll', 'save', 'duplicate'],
}

/** A unit of measure as the catalog keeps it. */
export interface Unit { id: string; code: string; name: string; active?: boolean }

/** The spec plus the head fields the read route returns and the write route takes back. */
export interface PackageDraft extends PackageSpec {
  description: string
  proposalText: string
  divisionId: string
  defaultQty: number
  defaultMeasure: number | null
  showAsOneLine: boolean
  editableAfterAdd: boolean
  optionalDefault: boolean
  rescaleOnMeasureChange: boolean
  termsId?: string | null
  active: boolean
  version: number
}

export const blankVisit = (name: string): PackageVisitSpec => ({
  name,
  role: 'work',
  timing: { kind: 'package_start' },
  daysRule: null,
  billingPlanKind: null,
  billingPlanId: null,
  notes: { quote: '', internal: '', crew: '' },
  optional: false,
  proposal: NO_PROPOSAL_OVERRIDES,
  services: [],
})

export const blankDraft = (divisionId: string): PackageDraft => ({
  id: 'new',
  name: '',
  description: '',
  proposalText: '',
  divisionId,
  asks: 'quantity',
  qtyUnit: 'each',
  defaultQty: 1,
  measurementTypeId: null,
  defaultMeasure: null,
  pricing: 'from_lines',
  flatPrice: null,
  showAsOneLine: true,
  editableAfterAdd: true,
  optionalDefault: false,
  rescaleOnMeasureChange: true,
  active: true,
  version: 0,
  plans: [],
  billingPlans: [],
  visits: [blankVisit('Visit 1')],
})

/** The one editor that is open, named by what it is editing — the quote's `Editing`, keyed by position. */
export type PackageEditing =
  | { kind: 'line'; lineId: string }
  | { kind: 'visit'; visitId: string; section?: 'name' | 'schedule' | 'billing' | 'notes' | 'choices' }
  | { kind: 'service'; serviceId: string }
  /** `afterLineId` / `afterServiceId`: a ⋮'s Add … below (item 4), as on the quote. */
  | { kind: 'add'; startKind?: 'item' | 'labor' | 'equipment' | 'deployment'; afterLineId?: string }
  | { kind: 'addService'; visitId: string; afterServiceId?: string }
  | { kind: 'addPackage' }
  | { kind: 'proposal'; visitId: string }
  | { kind: 'margin'; visitId: string }
  | { kind: 'settings' }
  /** A billing plan in the 480 popover (§10.2) — by its handle, or `new` for one being added. */
  | { kind: 'billingPlan'; handle: string }
  | null

/** The group a package's plans make on a quote — one question, its id a fixed word here. */
export const PLANS_GROUP = 'plans'

/** `v0.s1.l2` → [0, 1, 2]. */
export function indexOf(key: string): number[] {
  return key.split('.').map((part) => Number(part.slice(1)))
}

const today = () => new Date().toISOString().slice(0, 10)

/**
 * What a line scales by, from its rate (§6.8 item 2: *once* is a rate of 0)
 * and what the package asks for. "Both" keeps the line's own choice.
 */
export function scaleFor(rate: number, asks: PackageDraft['asks'], was: ScalesBy): ScalesBy {
  if (rate === 0 || asks === 'none') return 'once'
  if (asks === 'measurement') return 'measurement'
  if (asks === 'quantity') return 'quantity'
  return was === 'once' ? 'measurement' : was
}

/** The catalog row a line names, in the columns a quote line keeps it in. */
function refsOf(kind: PackageLineKind, refId: string) {
  return {
    itemId: kind === 'item' ? refId : null,
    laborRateId: kind === 'labor' || kind === 'sub' ? refId : null,
    equipmentId: kind === 'equipment' ? refId : null,
    rentalEquipmentId: kind === 'rental' ? refId : null,
    deploymentCostId: kind === 'deployment' ? refId : null,
  }
}

const quoteKind = (kind: PackageLineKind): QuoteLineKind => (kind === 'rental' ? 'equipment' : kind)

/** A visit's place in the draft — the id the views give it. */
const keyOf = (index: number) => `v${index}`
const isKey = (id: string) => /^v\d+$/.test(id)

/** Length on a package visit is its days rule (§10.1); a rule by the measure reads as one day. */
const lengthOf = (rule: DaysRule | null | undefined) => (rule?.kind === 'fixed' ? rule.days : 1)

/**
 * Every visit's When, as the quote's block reads it — `visitWhenOfPackage`
 * for a `when` timing, §10.1's table (`packageWhenOfOldTiming`) for the five
 * older kinds — keyed by the views' ids, so an anchor is one of them.
 * Nothing here is written: an old kind is only re-said, and stays what it is
 * until somebody edits that visit's When.
 */
export function packageWhens(visits: PackageVisitSpec[]): VisitForDates[] {
  const keyOfRef = new Map<string, string>()
  visits.forEach((visit, index) => {
    if (visit.timing.kind === 'when' && !keyOfRef.has(visit.timing.ref)) keyOfRef.set(visit.timing.ref, keyOf(index))
  })
  return visits.map((visit, index) => {
    const timing = visit.timing.kind === 'when'
      ? visit.timing
      : {
        ...packageWhenOfOldTiming(visit.timing, (from) => (index === 0 ? null
          : keyOf(from === 'previous' ? index - 1 : 0))),
        ref: keyOf(index),
      }
    return {
      id: keyOf(index),
      name: visit.name,
      when: visitWhenOfPackage(timing, (ref) => keyOfRef.get(ref), lengthOf(visit.daysRule)),
    }
  })
}

/**
 * The block's draft written back (§10.1): this visit becomes `kind: 'when'`
 * with the `ref` it already had or one made now, and an anchor still on an
 * older kind is written as the `when` it already reads as, so `anchorRef` has
 * a ref to name. Length goes back to the days rule, untouched when it did not
 * move — a rule by the measure is left alone.
 */
export function withWhen(
  visits: PackageVisitSpec[], index: number, when: VisitWhen,
  newRef: () => string = () => crypto.randomUUID(),
): PackageVisitSpec[] {
  const whens = packageWhens(visits)
  const next = visits.map((visit) => ({ ...visit }))
  const refOfKey = (id: string): string => {
    if (!isKey(id)) return id
    const at = Number(id.slice(1))
    const timing = next[at]?.timing
    if (!timing) return id
    if (timing.kind === 'when') return timing.ref
    const ref = newRef()
    next[at] = { ...next[at]!, timing: packageTimingOfWhen(whens[at]!.when, ref, refOfKey) }
    return ref
  }
  const self = next[index]!
  const ref = self.timing.kind === 'when' ? self.timing.ref : newRef()
  const was = lengthOf(self.daysRule)
  const length = Math.max(1, Math.round(when.lengthDays) || 1)
  next[index] = {
    ...next[index]!,
    timing: packageTimingOfWhen(when, ref, refOfKey),
    daysRule: length === was ? self.daysRule ?? null : { kind: 'fixed', days: length },
  }
  return next
}

/**
 * A visit taken out (§10.1): whatever started after it now starts *At the
 * start* — the quote's rule 2, a follower is never left pointing at nothing.
 */
export function withoutVisit(visits: PackageVisitSpec[], index: number): PackageVisitSpec[] {
  const gone = visits[index]?.timing
  const ref = gone?.kind === 'when' ? gone.ref : null
  return visits.filter((_, at) => at !== index).map((visit) => (
    ref && visit.timing.kind === 'when' && visit.timing.starts.kind === 'after' && visit.timing.starts.anchorRef === ref
      ? { ...visit, timing: { ...visit.timing, starts: { kind: 'start' as const } } }
      : visit))
}

/** A copy of a visit, with a `ref` of its own so nothing that followed the original follows it. */
export function copyOfVisit(visit: PackageVisitSpec, newRef: () => string = () => crypto.randomUUID()): PackageVisitSpec {
  const { id: _id, ...copy } = visit
  void _id
  return {
    ...copy,
    name: `${copy.name} copy`,
    timing: copy.timing.kind === 'when' ? { ...copy.timing, ref: newRef() } : copy.timing,
  }
}

/*
 * A service's and a line's ⋮ (Aaron, review item 4) over the draft: the four
 * pieces Duplicate, Add … below and Move up / down are made of. Pure, and
 * exported for their test, like the visit's above.
 */

/** A copy of a service and its lines, as new objects — editing one never edits the other. */
export const copyOfService = (service: PackageServiceSpec): PackageServiceSpec =>
  ({ ...service, lines: service.lines.map((line) => ({ ...line })) })

/** The service popover's three note keys, and the catalog note each one overrides. */
const NOTE_KEYS = [
  ['noteCustomer', 'customer'], ['noteInternal', 'internal'], ['noteCrew', 'crew'],
] as const

/**
 * The service popover's Done, as overrides on the package's service (0127).
 *
 * The popover sends a note only when its box moved, so a note it does not
 * send is left exactly as stored — following the catalog stays following
 * it. One it does send is the package's own unless it reads as the catalog's,
 * which `noteOverride` stores as null: typing the catalog's words back is
 * putting the package back on the catalog, not freezing a copy of them.
 */
export function serviceNotesPatch(
  service: PackageServiceSpec, patch: Record<string, unknown>,
): Pick<PackageServiceSpec, 'noteCustomer' | 'noteInternal' | 'noteCrew'> {
  const catalog = service.catalogNotes ?? NO_SERVICE_NOTES
  const out: Pick<PackageServiceSpec, 'noteCustomer' | 'noteInternal' | 'noteCrew'> = {}
  for (const [key, audience] of NOTE_KEYS) {
    const sent = patch[key]
    if (typeof sent === 'string') out[key] = noteOverride(sent, catalog[audience])
  }
  return out
}

/** `item` at `index`, everything from there on one further down. */
export const insertAt = <T>(list: T[], index: number, item: T): T[] =>
  [...list.slice(0, index), item, ...list.slice(index)]

/** The entry at `from` taken out and put back at `to`; out of range is no move. */
export function moved<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return list
  const next = [...list]
  const [held] = next.splice(from, 1)
  next.splice(to, 0, held!)
  return next
}

/** Where the entry that was at `at` is after `moved(list, from, to)`. */
export function placeAfterMove(at: number, from: number, to: number): number {
  if (at === from) return to
  if (from < to && at > from && at <= to) return at - 1
  if (from > to && at >= to && at < from) return at + 1
  return at
}

/** One visit's services, changed. */
export const onServices = (
  visits: PackageVisitSpec[], v: number, change: (list: PackageServiceSpec[]) => PackageServiceSpec[],
): PackageVisitSpec[] => visits.map((visit, at) => (at !== v ? visit : { ...visit, services: change(visit.services) }))

/** One service's lines, changed. */
export const onLines = (
  visits: PackageVisitSpec[], v: number, s: number, change: (list: PackageLineSpec[]) => PackageLineSpec[],
): PackageVisitSpec[] => onServices(visits, v, (services) => services.map((service, at) =>
  (at !== s ? service : { ...service, lines: change(service.lines) })))

/** A package's plan (Silver, Gold…): its id once saved, its key while it is new. */
export const planHandle = (plan: PackagePlanSpec, index: number) => plan.id ?? plan.key ?? `p${index}`

/** A package's billing plan: its id once saved, its key while it is new. */
export const billingHandle = (plan: PackageBillingPlanSpec, index: number) => plan.id ?? plan.key ?? `b${index}`

/**
 * The package's billing plans as the quote's plan views (§10.2), so the visit
 * popover's Billing field, the plan editor and the card's chip read them
 * without asking which page they are on. Money is the quote's to work out:
 * a package plan has no dates and no invoices.
 */
export function packagePlanViews(draft: PackageDraft): QuoteBillingPlanView[] {
  const keyOfRef = new Map<string, string>()
  draft.visits.forEach((visit, index) => {
    if (visit.timing.kind === 'when') keyOfRef.set(visit.timing.ref, keyOf(index))
  })
  return (draft.billingPlans ?? []).map((plan, index): QuoteBillingPlanView => {
    const id = billingHandle(plan, index)
    return {
      id,
      name: plan.name,
      kind: plan.kind,
      depositPct: plan.depositPct,
      termMonths: null,
      dayOfMonth: plan.dayOfMonth,
      dayOfWeek: plan.dayOfWeek,
      periodUnit: plan.periodUnit,
      periodEvery: plan.periodEvery,
      periodCount: plan.periodCount,
      periodBasis: plan.periodBasis,
      billFrom: null,
      billTo: null,
      tmTrigger: plan.tmTrigger,
      capAmount: plan.capAmount,
      termsDays: plan.termsDays,
      termsMode: plan.termsMode,
      completionMode: plan.completionMode,
      partPeriod: plan.partPeriod,
      depositFlat: plan.depositFlat,
      restMode: plan.restMode,
      // No customer yet: *the customer's terms* is a word on a package, never a number.
      customerTermsDays: 30,
      effectiveTermsDays: plan.termsMode === 'days' ? plan.termsDays : 30,
      sortOrder: index,
      milestones: plan.milestones.map((stage, at) => ({
        id: `${id}.m${at}`,
        sequence: at,
        name: stage.name,
        trigger: stage.trigger,
        dueOn: null,
        pct: stage.pct,
        amount: stage.amount,
        progressPct: stage.progressPct,
        visitId: stage.visitRef ? keyOfRef.get(stage.visitRef) ?? null : null,
      })),
      visitIds: draft.visits.flatMap((visit, at) => (visit.billingPlanId === id ? [keyOf(at)] : [])),
      total: 0,
      stageSum: null,
      window: null,
      invoices: [],
      ifAdded: null,
      allIn: null,
    }
  })
}

/** What the plan panel may write on a package plan — every setting but the billed window. */
const PLAN_FIELDS = [
  'name', 'completionMode', 'periodEvery', 'periodUnit', 'dayOfMonth', 'dayOfWeek', 'periodCount', 'periodBasis',
  'partPeriod', 'restMode', 'capAmount', 'tmTrigger', 'termsMode', 'termsDays',
] as const

/**
 * One of the plan panel's writes, onto the draft (§10.2). The panel is the
 * quote's (`KindPanel`), and it sends what the quote's plan route takes; this
 * is that route's work for a package: the kind clears what only another kind
 * may hold (the CHECKs `package_billing_plans` copied), a plan still wearing
 * its kind's default name follows the kind, a deposit is one basis, and a
 * stage that names a visit names its `ref` — making one if the visit has none
 * yet, which writes that visit's When as the `when` it already reads as.
 */
export function withPlanPatch(
  draft: PackageDraft, handle: string, body: Record<string, unknown>,
  newRef: () => string = () => crypto.randomUUID(),
): PackageDraft {
  const plans = draft.billingPlans ?? []
  const at = plans.findIndex((plan, index) => billingHandle(plan, index) === handle)
  if (at < 0) return draft
  const was = plans[at]!
  const next: PackageBillingPlanSpec = { ...was }
  let visits = draft.visits

  for (const field of PLAN_FIELDS) {
    if (field in body) (next as unknown as Record<string, unknown>)[field] = body[field]
  }
  if ('depositPct' in body) { next.depositPct = body.depositPct as number | null; next.depositFlat = null }
  if ('depositFlat' in body) { next.depositFlat = body.depositFlat as number | null; next.depositPct = null }
  if ('kind' in body && body.kind !== was.kind) {
    next.kind = body.kind as BillingPlanKind
    if (next.kind !== 'deposit_balance') { next.depositPct = null; next.depositFlat = null }
    if (next.kind !== 'time_materials') next.capAmount = null
  }
  if (was.name === defaultPlanName(was.kind, was.periodUnit) && !('name' in body)) {
    next.name = defaultPlanName(next.kind, next.periodUnit)
  }
  if (Array.isArray(body.milestones)) {
    const refOf = (key: string): string | null => {
      if (!isKey(key)) return null
      const index = Number(key.slice(1))
      const timing = visits[index]?.timing
      if (!timing) return null
      if (timing.kind === 'when') return timing.ref
      visits = withWhen(visits, index, packageWhens(visits)[index]!.when, newRef)
      const made = visits[index]!.timing
      return made.kind === 'when' ? made.ref : null
    }
    next.milestones = (body.milestones as {
      name: string; trigger: MilestoneTrigger; pct: number | null; amount: number | null
      progressPct: number | null; visitId: string | null
    }[]).map((stage) => {
      const trigger = stage.trigger === 'on_date' ? 'on_approval' : stage.trigger
      return {
        name: stage.name,
        trigger,
        pct: stage.pct,
        amount: stage.amount,
        progressPct: trigger === 'progress' ? stage.progressPct : null,
        visitRef: trigger === 'visit_completed' && stage.visitId ? refOf(stage.visitId) : null,
      }
    })
  }
  return {
    ...draft,
    visits,
    billingPlans: plans.map((plan, index) => (index === at ? next : plan)),
  }
}

/**
 * The draft and its preview, as the quote's tree.
 *
 * Pure, and exported for its test: the engine draws whatever this hands it,
 * so this is where "a package visit reads like a quote visit" is true or not.
 */
export function packageTree(
  draft: PackageDraft,
  preview: PackagePreviewResponse | null,
  sent: number[],
  divisions: Division[],
  /** The sample the lines resolve at — the measure chip on each service (Board 5). */
  sample: { measure: number; unit: string | null } | null = null,
): Pick<QuoteTree, 'visits' | 'chooseGroups' | 'chooseOptions'> & { targetMarginPct: number } {
  const plans = draft.plans ?? []
  const previewOf = (index: number) => {
    const at = sent.indexOf(index)
    return at < 0 ? undefined : preview?.visits[at]
  }

  /*
   * Every visit's When, resolved over the whole package the way the quote
   * resolves its own (§10.1): no dates yet, so a start is *floating* and the
   * words are the "no date yet" row of §4.4 — "Every 6 weeks · 5 visits".
   */
  const whens = packageWhens(draft.visits)
  const resolvedAll = resolveVisitDates(whens, { rentalEnd: null })
  const names = new Map(whens.map((one) => [one.id, one.name]))
  const billingPlans = packagePlanViews(draft)

  const visits = draft.visits.map((visit, index): QuoteVisitView => {
    const figures = previewOf(index)
    const key = keyOf(index)
    const when = whens[index]!.when
    const resolved = resolvedAll.get(key)!
    const phrase = visitWhenPhrase(when, { names, resolved })
    const openEnded = resolved.occurrences === null
    const trips = openEnded ? null : figures?.trips ?? resolved.occurrences ?? 1
    const plan = billingPlans.find((one) => one.id === visit.billingPlanId) ?? null
    return {
      id: key,
      name: visit.name,
      autoNamed: false,
      qty: trips ?? 1,
      schedule: 'one_time',
      startsOn: null,
      endsOn: null,
      durationDays: null,
      targetOn: null,
      occurrences: openEnded ? Number.POSITIVE_INFINITY : trips ?? 1,
      // The × box is a fixed count's; every other rule's count is the rule's.
      countScheduled: !(when.repeats.kind === 'every' && when.ends.kind === 'count'),
      scheduleLabel: phrase.card,
      when,
      resolved,
      phrase,
      billingPlanId: plan?.id ?? null,
      billingPlanName: plan?.name ?? null,
      optional: visit.optional ?? false,
      /*
       * An optional visit is an extra on whichever plan is picked, never one
       * of the answers (step 24 item 1) — so it sits outside the grid, as
       * `addPackageToQuote` puts it outside the question.
       */
      chooseGroupId: plans.length > 0 && !visit.optional ? PLANS_GROUP : null,
      chooseGroupName: plans.length > 0 && !visit.optional
        ? draft.proposalText || DEFAULT_QUESTION_NAME : null,
      declined: false,
      noteQuote: visit.notes.quote,
      noteInternal: visit.notes.internal,
      noteCrew: visit.notes.crew,
      proposal: visit.proposal ?? NO_PROPOSAL_OVERRIDES,
      role: visit.role,
      trigger: 'date',
      packageInstanceId: null,
      packageVisitId: visit.id ?? null,
      generated: false,
      openEnded,
      sortOrder: index,
      /*
       * One trip's money, as Board 5 draws the card ("$121" over "× 5
       * visits"); a quote's card carries every trip's. The hours are every
       * trip's, because the card prints them per visit by dividing.
       */
      total: figures?.total ?? 0,
      cost: figures?.cost ?? 0,
      hours: (figures?.hours ?? 0) * (openEnded ? 1 : trips ?? 1),
      marginPct: figures?.margin.withOptions.marginPct ?? null,
      margin: figures?.margin ?? { base: null, withOptions: EMPTY_BLOCK, lowest: [] },
      services: visit.services.map((service, serviceIndex) =>
        serviceView(service, `${key}.s${serviceIndex}`, key, serviceIndex,
          figures?.services[serviceIndex], divisions,
          draft.asks === 'measurement' || draft.asks === 'both' ? sample : null,
          draft.measurementTypeId)),
    }
  })

  return {
    visits,
    chooseGroups: plans.length > 0
      ? [{ id: PLANS_GROUP, name: draft.proposalText || DEFAULT_QUESTION_NAME, allowNone: false }]
      : [],
    chooseOptions: plans.map((plan, index) => {
      const handle = planHandle(plan, index)
      return {
        id: handle,
        groupId: PLANS_GROUP,
        name: plan.name,
        tagline: plan.tagline ?? '',
        recommended: plan.recommended ?? false,
        declined: false,
        visitIds: draft.visits.flatMap((visit, at) => {
          if (visit.optional) return []
          const named = visit.planIds ?? []
          return named.length === 0 || named.includes(handle) ? [`v${at}`] : []
        }),
        total: preview?.margin.byChoice[0]?.options.find((one) => one.id === handle)?.sold ?? 0,
      }
    }),
    targetMarginPct: preview?.targetMarginPct ?? 36,
  }
}

const EMPTY_BLOCK = { sold: 0, cost: 0, gross: 0, marginPct: null, hours: 0, kinds: [] }

function serviceView(
  service: PackageServiceSpec, key: string, visitKey: string, sortOrder: number,
  figures: PackagePreviewResponse['visits'][number]['services'][number] | undefined,
  divisions: Division[],
  sample: { measure: number; unit: string | null } | null,
  measurementTypeId: string | null,
): QuoteServiceView {
  const division = divisions.find((one) => one.id === service.divisionId)
  const notes = packageServiceNotes(service)
  return {
    id: key,
    visitId: visitKey,
    name: service.name ?? '',
    divisionId: service.divisionId,
    divisionName: division?.name ?? '',
    divisionCode: (division as { code?: string } | undefined)?.code ?? '',
    serviceId: service.serviceId,
    serviceName: service.name ?? null,
    // A package's service is measured at the package's sample — the chip shows it, Try it changes it.
    measure: sample ? sample.measure : null,
    measureUnit: sample?.unit ?? null,
    measurementId: null,
    measurementTypeId: sample ? measurementTypeId : null,
    measurementName: null,
    propertyMeasure: null,
    optional: service.optional ?? false,
    taxable: false,
    sortOrder,
    /*
     * The words the quote will get (0127): the package's own where it wrote
     * some, the catalog service's where it did not — so the popover's Notes
     * block opens on what will actually print, and `serviceNotesPatch` turns
     * an edit back into an override.
     */
    noteCustomer: notes.customer,
    noteInternal: notes.internal,
    noteCrew: notes.crew,
    total: figures?.total ?? 0,
    cost: figures?.cost ?? 0,
    hours: figures?.lines.reduce((sum, line) => sum + (line.optional ? 0 : line.hours), 0) ?? 0,
    marginPct: figures?.marginPct ?? null,
    lines: service.lines.map((line, lineIndex) =>
      lineView(line, `${key}.l${lineIndex}`, lineIndex, figures?.lines[lineIndex])),
  }
}

function lineView(
  line: PackageLineSpec, key: string, sortOrder: number, figures: PackagePreviewLine | undefined,
): QuoteLineView {
  return {
    id: key,
    kind: figures?.kind ?? quoteKind(line.kind),
    description: figures?.name ?? line.name ?? '',
    // The recipe line's own (0126); the grid prints `description` either way.
    customerName: line.customerName ?? null,
    ...refsOf(line.kind, line.refId),
    qty: figures?.qty ?? 0,
    unit: figures?.unit ?? '',
    hours: figures?.hours ?? 0,
    standardRate: figures?.standardRate ?? 0,
    rate: figures?.rate ?? 0,
    amount: figures?.amount ?? 0,
    cost: figures?.cost ?? 0,
    priceEdited: line.priceOverride !== null && line.priceOverride !== undefined,
    editReason: null,
    handEdited: false,
    handling: figures?.handling ?? line.handling ?? null,
    optional: line.optional,
    taxable: figures?.taxable ?? false,
    taxed: figures?.taxable ?? false,
    showOnProposal: line.showOnProposal,
    sortOrder,
    note: '',
    buying: null,
    packageLineId: null,
    scalesBy: line.scalesBy,
    factor: figures?.factor ?? null,
    costEach: figures?.costEach ?? null,
    marginPct: figures?.marginPct ?? null,
    gross: figures?.gross ?? 0,
  }
}

/** What the picker handed back, as a recipe line: once, one of it. */
export function lineFromPick(pick: Pickable): PackageLineSpec {
  const body = pick.body as Record<string, string | undefined>
  const refId = body.itemId ?? body.laborRateId ?? body.equipmentId
    ?? body.rentalEquipmentId ?? body.deploymentCostId ?? ''
  return {
    /*
     * The picker's "equipment" is a row of the equipment catalog, owned kind or
     * hired in alike (0129) — `rental` is the line kind that names that table —
     * and a machine only where an older picker still sent one.
     */
    kind: pick.kind === 'equipment' && body.rentalEquipmentId
      ? 'rental'
      : (pick.kind as PackageLineKind),
    refId,
    name: pick.label,
    scalesBy: 'once',
    baseQty: 1,
    qtyPerUnit: 0,
    bands: [],
    priceOverride: null,
    optional: false,
    showOnProposal: true,
    taxable: null,
    handling: null,
    customerName: null,
  }
}

/** A catalog service's recipe line, as `GET /catalog-admin/services/:id` reads it. */
interface RecipeLine {
  kind: PackageLineKind
  itemId?: string | null; laborRateId?: string | null; equipmentId?: string | null
  rentalEquipmentId?: string | null; deploymentCostId?: string | null
  name?: string; scalesBy?: ScalesBy; baseQty: string | number; qtyPerUnit: string | number
  bands: { minMeasure: string | number; baseQty: string | number; qtyPerUnit: string | number }[]
  optional?: boolean; showOnProposal?: boolean; taxable?: boolean | null
  priceOverride?: string | number | null; handling?: LineHandling | null
  customerName?: string | null
}

function recipeToLine(line: RecipeLine, asks: PackageDraft['asks']): PackageLineSpec {
  const rate = Number(line.qtyPerUnit) || 0
  return {
    kind: line.kind,
    refId: line.itemId ?? line.laborRateId ?? line.equipmentId ?? line.rentalEquipmentId
      ?? line.deploymentCostId ?? '',
    name: line.name,
    scalesBy: scaleFor(rate, asks, line.scalesBy ?? 'measurement'),
    baseQty: Number(line.baseQty) || 0,
    qtyPerUnit: rate,
    bands: line.bands.map((band): QuantityBand => ({
      minMeasure: Number(band.minMeasure) || 0,
      baseQty: Number(band.baseQty) || 0,
      qtyPerUnit: Number(band.qtyPerUnit) || 0,
    })),
    priceOverride: line.priceOverride === null || line.priceOverride === undefined
      ? null : Number(line.priceOverride),
    optional: line.optional ?? false,
    showOnProposal: line.showOnProposal ?? true,
    taxable: line.taxable ?? null,
    handling: line.handling ?? null,
    // A service's recipe line carries its customer-facing name into the package (0126).
    customerName: line.customerName ?? null,
  }
}

/** A package line, as the Line popover edits it — the service recipe's draft (§6.3). */
export function recipeDraftOf(line: PackageLineSpec): RecipeLineDraft {
  const { tiers, stacked } = draftTiersOf(line)
  const shown = (value: number) => String(Number(value.toFixed(6)))
  return {
    base: shown(line.baseQty),
    rate: shown(line.qtyPerUnit),
    tiers,
    legacyBands: stacked ? null : line.bands.map((band) => ({
      minMeasure: shown(band.minMeasure), baseQty: shown(band.baseQty), qtyPerUnit: shown(band.qtyPerUnit),
    })),
    optional: line.optional,
    showOnProposal: line.showOnProposal,
    taxable: line.taxable ?? null,
    priceOverride: line.priceOverride ?? null,
    handling: line.handling ?? null,
    customerName: line.customerName ?? null,
  }
}

/** …and back. The scale follows the rate, as everywhere (a rate of 0 is *once*). */
export function lineOfRecipeDraft(
  draft: RecipeLineDraft, line: PackageLineSpec, asks: PackageDraft['asks'],
): PackageLineSpec {
  const qtyPerUnit = readNumber(draft.rate)
  return {
    ...line,
    baseQty: readNumber(draft.base),
    qtyPerUnit,
    bands: bandsOfDraft(draft),
    scalesBy: scaleFor(qtyPerUnit, asks, line.scalesBy),
    optional: draft.optional,
    showOnProposal: draft.showOnProposal,
    taxable: draft.taxable,
    priceOverride: draft.priceOverride,
    handling: line.kind === 'item' ? draft.handling : null,
    customerName: draft.customerName,
  }
}

/** Everything the write route takes, from a draft. */
export function bodyOf(draft: PackageDraft) {
  /*
   * The division follows the services: `packages.division_id` is one column
   * and takes the first service's, so the package files under the division
   * that does the first piece of work.
   */
  const first = draft.visits.flatMap((visit) => visit.services).map((one) => one.divisionId).find(Boolean)
  return {
    name: draft.name,
    description: draft.description,
    proposalText: draft.proposalText,
    divisionId: first ?? draft.divisionId,
    asks: draft.asks,
    qtyUnit: draft.qtyUnit,
    defaultQty: draft.defaultQty,
    measurementTypeId: draft.measurementTypeId,
    defaultMeasure: draft.defaultMeasure,
    pricing: draft.pricing,
    flatPrice: draft.flatPrice,
    showAsOneLine: draft.showAsOneLine,
    editableAfterAdd: draft.editableAfterAdd,
    optionalDefault: draft.optionalDefault,
    rescaleOnMeasureChange: draft.rescaleOnMeasureChange,
    termsId: draft.termsId ?? null,
    active: draft.active,
    plans: draft.plans ?? [],
    billingPlans: draft.billingPlans ?? [],
    visits: draft.visits,
  }
}

/**
 * What the preview may be asked about: the visits that hold a service whose
 * every line names something. A visit just added is empty, and the write
 * schema refuses an empty visit — so it waits out of the preview, and `sent`
 * says which draft visit each answer belongs to.
 */
export function previewable(draft: PackageDraft): { body: ReturnType<typeof bodyOf>; sent: number[] } | null {
  const sent: number[] = []
  const visits = draft.visits.filter((visit, index) => {
    const ok = visit.services.length > 0
      && visit.services.every((service) => service.serviceId && service.lines.every((line) => line.refId))
    if (ok) sent.push(index)
    return ok
  })
  if (visits.length === 0) return null
  return { body: { ...bodyOf(draft), name: draft.name || 'Draft', visits }, sent }
}

export type PackageBuilder = ReturnType<typeof usePackageBuilder>

export function usePackageBuilder(me: MeResponse) {
  const { id = 'new' } = useParams()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const phone = usePhone()
  const narrow = useNarrow(1180)

  const [draft, setDraft] = useState<PackageDraft | null>(null)
  const [divisions, setDivisions] = useState<Division[]>([])
  const [types, setTypes] = useState<MeasurementType[]>([])
  const [units, setUnits] = useState<Unit[]>([])
  const [error, setError] = useState<string | null>(null)
  const [warnings, setWarnings] = useState<{ visit: string; line: string; message: string }[]>([])
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState<PackagePreviewResponse | null>(null)
  const [sent, setSent] = useState<number[]>([])
  /** The sample every figure is worked out at — **Try it with** (cb-05 ②). */
  const [sample, setSample] = useState<{ qty: number; measure: number }>({ qty: 1, measure: 0 })

  const [selected, setSelected] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [railOn, setRailOn] = useState(true)
  const [menu, setMenu] = useState<{ items: MenuItem[]; x: number; y: number } | null>(null)
  const [editing, setEditing] = useState<PackageEditing>(null)
  const [marginScope, setMarginScope] = useState<MarginScope>('base')
  const [summaryOpen, setSummaryOpen] = useState(false)
  const [openService, setOpenService] = useState<string | null>(null)
  const anchor = useRef<HTMLElement | null>(null)
  const searchBox = useRef<HTMLButtonElement | null>(null)
  const packageBox = useRef<HTMLSpanElement | null>(null)

  const canEdit = me.permissions.company_settings === 'edit' || me.permissions.company_settings === 'full'
  const openVisitId = params.get('visit')
  const openPhoneVisit = (visitId: string | null) => setParams((was) => {
    const next = new URLSearchParams(was)
    if (visitId) next.set('visit', visitId)
    else next.delete('visit')
    return next
  })

  const load = useCallback(async () => {
    const [divisionRows, typeRows, unitRows] = await Promise.all([
      request<{ items: Division[] }>('/catalog-admin/divisions').catch(() => ({ items: [] })),
      request<{ items: MeasurementType[] }>('/catalog-admin/measurement-types').catch(() => ({ items: [] })),
      request<{ items: Unit[] }>('/catalog-admin/units').catch(() => ({ items: [] })),
    ])
    setDivisions(divisionRows.items)
    setTypes(typeRows.items)
    setUnits(unitRows.items.filter((one) => one.active !== false))
    const start = (found: PackageDraft) => {
      setDraft(found)
      setSample({ qty: found.defaultQty || 1, measure: found.defaultMeasure ?? 0 })
    }
    if (id === 'new') { start(blankDraft(divisionRows.items[0]?.id ?? '')); return }
    try {
      start((await request<{ package: PackageDraft }>(`/catalog-admin/packages/${id}`)).package)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That package would not open.')
    }
  }, [id])

  useEffect(() => { void load() }, [load])

  /*
   * Every figure on the page, from the server, a quarter-second after the
   * last edit (§7.3). A late answer to a superseded question is dropped.
   */
  useEffect(() => {
    if (!draft) return undefined
    const asked = previewable(draft)
    if (!asked) { setPreview(null); setSent([]); return undefined }
    let live = true
    const timer = window.setTimeout(() => {
      void request<PackagePreviewResponse>(`/catalog-admin/packages/${id}/preview`, {
        method: 'POST',
        body: { qty: sample.qty, measure: sample.measure, startsOn: today(), draft: asked.body },
      })
        .then((answer) => { if (live) { setPreview(answer); setSent(asked.sent) } })
        .catch(() => { /* the last good figures stay; Save reports what is wrong */ })
    }, 250)
    return () => { live = false; window.clearTimeout(timer) }
  }, [draft, sample, id])

  const tree = useMemo(
    () => (draft
      ? packageTree(draft, preview, sent, divisions, {
        measure: sample.measure,
        unit: types.find((one) => one.id === draft.measurementTypeId)?.unit ?? null,
      })
      : null),
    [draft, preview, sent, divisions, sample, types])

  // -------------------------------------------------------------------------
  // The draft's edits
  // -------------------------------------------------------------------------

  const patch = (over: Partial<PackageDraft>) => setDraft((was) => (was ? { ...was, ...over } : was))
  const patchVisits = (change: (visits: PackageVisitSpec[]) => PackageVisitSpec[]) =>
    setDraft((was) => (was ? { ...was, visits: change(was.visits) } : was))
  const patchVisit = (index: number, over: Partial<PackageVisitSpec>) =>
    patchVisits((visits) => visits.map((visit, at) => (at === index ? { ...visit, ...over } : visit)))
  const patchService = (key: string, over: Partial<PackageServiceSpec>) => {
    const [v, s] = indexOf(key)
    patchVisits((visits) => visits.map((visit, at) => (at !== v ? visit : {
      ...visit,
      services: visit.services.map((service, one) => (one === s ? { ...service, ...over } : service)),
    })))
  }
  const patchLine = (key: string, over: Partial<PackageLineSpec>) => {
    const [v, s, l] = indexOf(key)
    patchVisits((visits) => visits.map((visit, at) => (at !== v ? visit : {
      ...visit,
      services: visit.services.map((service, one) => (one !== s ? service : {
        ...service,
        lines: service.lines.map((line, which) => (which === l ? { ...line, ...over } : line)),
      })),
    })))
  }
  const removeLine = (key: string) => {
    const [v, s, l] = indexOf(key)
    patchVisits((visits) => visits.map((visit, at) => (at !== v ? visit : {
      ...visit,
      services: visit.services.map((service, one) => (one !== s ? service : {
        ...service, lines: service.lines.filter((_, which) => which !== l),
      })),
    })))
  }
  const removeService = (key: string) => {
    const [v, s] = indexOf(key)
    patchVisits((visits) => visits.map((visit, at) => (at !== v ? visit : {
      ...visit, services: visit.services.filter((_, one) => one !== s),
    })))
  }
  /** A rule's two numbers; the scale follows the rate (a rate of 0 is *once*). */
  const setRule = (key: string, base: number | null, rate: number | null) => {
    const [v, s, l] = indexOf(key)
    const line = draft?.visits[v!]?.services[s!]?.lines[l!]
    if (!line || !draft) return
    const qtyPerUnit = rate ?? line.qtyPerUnit
    patchLine(key, {
      baseQty: base ?? line.baseQty,
      qtyPerUnit,
      scalesBy: scaleFor(qtyPerUnit, draft.asks, line.scalesBy),
    })
  }

  const addVisit = (after?: number) => patchVisits((visits) => {
    const made = { ...blankVisit(`Visit ${visits.length + 1}`), optional: draft?.optionalDefault ?? false }
    const at = after === undefined ? visits.length : after + 1
    return [...visits.slice(0, at), made, ...visits.slice(at)]
  })
  /** A visit out, its followers starting *At the start* (§10.1). */
  const removeVisit = (index: number) => patchVisits((visits) => withoutVisit(visits, index))
  const moveVisit = (from: number, to: number) => patchVisits((visits) => {
    if (to < 0 || to >= visits.length) return visits
    const next = [...visits]
    const [held] = next.splice(from, 1)
    next.splice(to, 0, held!)
    return next
  })

  /*
   * A service's fold follows it. Its key is its place (`v0.s1`), so a folded
   * service moved down — or pushed down by a copy put above it — would leave
   * its fold behind on whatever took its place (item 4's Collapse and Move).
   */
  const refold = (v: number, placeOf: (was: number) => number) => setCollapsed((was) => {
    const prefix = `v${v}.s`
    return new Set([...was].map((key) => (key.startsWith(prefix) && !key.slice(prefix.length).includes('.')
      ? `${prefix}${placeOf(Number(key.slice(prefix.length)))}`
      : key)))
  })
  /* A ⋮'s Duplicate and Move up / down (item 4), over the draft. */
  const duplicateService = (key: string) => {
    const [v, s] = indexOf(key)
    patchVisits((visits) => onServices(visits, v!, (list) => insertAt(list, s! + 1, copyOfService(list[s!]!))))
    refold(v!, (at) => (at > s! ? at + 1 : at))
  }
  const duplicateLine = (key: string) => {
    const [v, s, l] = indexOf(key)
    patchVisits((visits) => onLines(visits, v!, s!, (list) => insertAt(list, l! + 1, { ...list[l!]! })))
  }
  const moveService = (key: string, to: number) => {
    const [v, s] = indexOf(key)
    patchVisits((visits) => onServices(visits, v!, (list) => moved(list, s!, to)))
    refold(v!, (at) => placeAfterMove(at, s!, to))
  }
  const moveLine = (key: string, to: number) => {
    const [v, s, l] = indexOf(key)
    patchVisits((visits) => onLines(visits, v!, s!, (list) => moved(list, l!, to)))
  }

  /**
   * A catalog service onto a visit, recipe and all — what the quote does.
   * `afterServiceId` is a service ⋮'s **Add a service below** (item 4).
   */
  async function addService(visitKey: string, pick: Pickable, afterServiceId?: string) {
    const [v] = indexOf(visitKey)
    const serviceId = String(pick.body.serviceId ?? '')
    const count = draft?.visits[v!]?.services.length ?? 0
    const place = afterServiceId ? indexOf(afterServiceId)[1]! + 1 : count
    try {
      const found = await request<{
        divisionId: string; name: string; lines: RecipeLine[]
        description?: string; noteInternal?: string; noteCrew?: string
      }>(`/catalog-admin/services/${serviceId}`)
      patchVisits((visits) => onServices(visits, v!, (list) => insertAt(list, place, {
        serviceId,
        divisionId: found.divisionId,
        name: found.name,
        optional: false,
        // Following the catalog (0127), so its popover opens on the catalog's words.
        catalogNotes: {
          customer: found.description ?? '',
          internal: found.noteInternal ?? '',
          crew: found.noteCrew ?? '',
        },
        lines: found.lines.map((line) => recipeToLine(line, draft?.asks ?? 'quantity')),
      })))
      if (place < count) refold(v!, (at) => (at >= place ? at + 1 : at))
      setOpenService(`${visitKey}.s${place}`)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That service would not open.')
    }
    setEditing(null)
  }

  /**
   * A line into the open service, or a service picked from the search onto
   * the last visit. `afterLineId` is a line ⋮'s **Add an item below** (item
   * 4): into that line's service, directly under it.
   */
  async function add(pick: Pickable, afterLineId?: string) {
    if (!draft) return
    if (pick.kind === 'service') {
      await addService(`v${Math.max(0, draft.visits.length - 1)}`, pick)
      return
    }
    const target = (afterLineId ? afterLineId.split('.').slice(0, 2).join('.') : null) ?? openService ?? (() => {
      const v = draft.visits.length - 1
      const s = (draft.visits[v]?.services.length ?? 0) - 1
      return v >= 0 && s >= 0 ? `v${v}.s${s}` : null
    })()
    if (!target) {
      setError('Add a service first — a line belongs to a piece of the work.')
      return
    }
    const [v, s] = indexOf(target)
    const service = draft.visits[v!]?.services[s!]
    const place = afterLineId ? indexOf(afterLineId)[2]! + 1 : service?.lines.length ?? 0
    if (service) patchService(target, { lines: insertAt(service.lines, place, lineFromPick(pick)) })
    setEditing(null)
  }

  /** Another package's visits, appended — a package is visits, so adding one is adding those. */
  async function addPackage(pick: Pickable) {
    try {
      const found = await request<{ package: PackageDraft }>(
        `/catalog-admin/packages/${String(pick.body.packageId ?? '')}`)
      patchVisits((visits) => [...visits, ...found.package.visits.map((visit) => {
        const { id: _id, ...rest } = visit
        void _id
        return { ...rest, planIds: [] }
      })])
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That package would not open.')
    }
    setEditing(null)
  }

  // Plans — the quote's plans grid, over the package's own plans (§10.6).
  const plansOf = () => draft?.plans ?? []
  const addPlan = (name: string): string => {
    const key = `new-${Date.now().toString(36)}`
    patch({ plans: [...plansOf(), { key, name, tagline: '', recommended: false }] })
    return key
  }
  const editPlan = (handle: string, over: Partial<PackagePlanSpec>) => patch({
    plans: plansOf().map((plan, index) => (planHandle(plan, index) === handle ? { ...plan, ...over } : plan)),
  })
  const recommendPlan = (handle: string, on = true) => patch({
    plans: plansOf().map((plan, index) => ({
      ...plan,
      recommended: planHandle(plan, index) === handle ? on : on ? false : plan.recommended,
    })),
  })
  const removePlan = (handle: string, keepVisits: boolean) => setDraft((was) => {
    if (!was) return was
    const plans = (was.plans ?? []).filter((plan, index) => planHandle(plan, index) !== handle)
    const visits = was.visits
      .filter((visit) => keepVisits || !((visit.planIds ?? []).length === 1 && visit.planIds![0] === handle))
      .map((visit) => ({ ...visit, planIds: (visit.planIds ?? []).filter((one) => one !== handle) }))
    return { ...was, plans, visits }
  })
  /** The whole set of plans a visit is part of — never a delta (§10.6). All of them is none. */
  const setMembership = (visitKey: string, optionIds: string[]) => {
    const [v] = indexOf(visitKey)
    const all = plansOf().map(planHandle)
    patchVisit(v!, { planIds: all.every((one) => optionIds.includes(one)) ? [] : optionIds })
  }

  /*
   * Billing plans (§10.2): added here, edited through the quote's plan panel
   * (`withPlanPatch`), removed with every visit on them falling back to the
   * quote's own plan — which is what a visit with no plan does on a quote.
   */
  const billingOf = () => draft?.billingPlans ?? []
  const addBillingPlan = (kind: BillingPlanKind = 'completion'): string => {
    const key = `bill-${Date.now().toString(36)}`
    patch({ billingPlans: [...billingOf(), { ...blankPackageBillingPlan(defaultPlanName(kind), kind), key }] })
    return key
  }
  const editBillingPlan = (handle: string, body: Record<string, unknown>) =>
    setDraft((was) => (was ? withPlanPatch(was, handle, body) : was))
  const removeBillingPlan = (handle: string) => setDraft((was) => {
    if (!was) return was
    return {
      ...was,
      billingPlans: (was.billingPlans ?? []).filter((plan, index) => billingHandle(plan, index) !== handle),
      visits: was.visits.map((visit) => (visit.billingPlanId === handle ? { ...visit, billingPlanId: null } : visit)),
    }
  })
  /** A visit's When, from the quote's block (§10.1) — written only when it changed, by the popover. */
  const setVisitWhen = (visitKey: string, when: VisitWhen) => setDraft((was) => {
    if (!was) return was
    const [v] = indexOf(visitKey)
    return { ...was, visits: withWhen(was.visits, v!, when) }
  })
  /** Whether a rental's end is something a visit here can follow: a rental line, or a visit already saying so. */
  const hasRental = (draft?.visits ?? []).some((visit) => visit.services.some((service) =>
    service.lines.some((line) => line.kind === 'rental'))
    || (visit.timing.kind === 'recurring' && visit.timing.while === 'rental') || visit.timing.kind === 'rental_end'
    || (visit.timing.kind === 'when' && (visit.timing.starts.kind === 'term_end' || visit.timing.ends.kind === 'term_end')))

  // -------------------------------------------------------------------------
  // Saving
  // -------------------------------------------------------------------------

  async function save() {
    if (!draft) return
    setBusy(true)
    setError(null)
    try {
      const body = bodyOf(draft)
      const made = id === 'new'
        ? await request<{ package: { id: string }; warnings: typeof warnings }>(
          '/catalog-admin/packages', { method: 'POST', body })
        : await request<{ package: { id: string }; warnings: typeof warnings }>(
          `/catalog-admin/packages/${id}`, { method: 'PUT', body: { ...body, version: draft.version } })
      setWarnings(made.warnings)
      if (id === 'new') navigate(`/settings/catalog/packages/${made.package.id}`, { replace: true })
      else await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That did not save.')
    } finally {
      setBusy(false)
    }
  }

  /** Another package like this one — the server's own create, with this spec and a new name. */
  async function duplicate() {
    if (!draft) return
    setBusy(true)
    setError(null)
    try {
      const made = await request<{ package: { id: string } }>('/catalog-admin/packages',
        { method: 'POST', body: { ...bodyOf(draft), name: `${draft.name} (copy)` } })
      navigate(`/settings/catalog/packages/${made.package.id}`)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That would not copy.')
    } finally {
      setBusy(false)
    }
  }

  // -------------------------------------------------------------------------
  // Opening things
  // -------------------------------------------------------------------------

  const at = (event?: unknown) => {
    if (event && typeof event === 'object' && 'currentTarget' in event) {
      anchor.current = (event as { currentTarget: HTMLElement }).currentTarget
    }
  }
  const openVisit = (visitId: string, section: 'name' | 'schedule' | 'billing' | 'notes' | 'choices') =>
    (event?: unknown) => { at(event); setSelected(visitId); setEditing({ kind: 'visit', visitId, section }) }
  const openServiceEditor = (serviceId: string) =>
    (event?: unknown) => { at(event); setOpenService(serviceId); setEditing({ kind: 'service', serviceId }) }
  const openLine = (lineId: string) => (event?: unknown) => {
    at(event)
    setOpenService(lineId.split('.').slice(0, 2).join('.'))
    setEditing({ kind: 'line', lineId })
  }
  const openAdd = (serviceId: string, kind: QuoteLineKind, from: HTMLElement) => {
    setOpenService(serviceId)
    anchor.current = from
    setEditing({ kind: 'add', startKind: kind === 'sub' ? undefined : kind })
  }
  const openMargin = (visitId: string, from: HTMLElement) => {
    anchor.current = from
    setEditing((was) => (was?.kind === 'margin' && was.visitId === visitId ? null : { kind: 'margin', visitId }))
  }
  /** "Fix them ›" — the first line with no cost, in its own popover. */
  const fixCosts = (lineId: string) => {
    anchor.current = document.querySelector(`[data-line-id="${lineId}"]`)
    setEditing({ kind: 'line', lineId })
  }

  const toggle = (key: string) => setCollapsed((was) => {
    const next = new Set(was)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
  })
  const everything = (tree?.visits ?? []).flatMap((visit) => [visit.id, ...visit.services.map((one) => one.id)])
  const allCollapsed = everything.length > 0 && everything.every((key) => collapsed.has(key))
  const collapseAll = () => setCollapsed(allCollapsed ? new Set() : new Set(everything))

  /** The ⋮ on a visit — Board 7's menu. */
  function visitMenu(visit: QuoteVisitView): MenuItem[] {
    if (!canEdit || !draft) return []
    const [index] = indexOf(visit.id)
    const i = index!
    return [
      { key: 'rename', label: 'Rename', onSelect: () => { setSelected(visit.id); setEditing({ kind: 'visit', visitId: visit.id, section: 'name' }) } },
      {
        key: 'duplicate',
        label: 'Duplicate',
        onSelect: () => patchVisits((visits) =>
          [...visits.slice(0, i + 1), copyOfVisit(visits[i]!), ...visits.slice(i + 1)]),
      },
      { key: 'after', label: 'Add a visit after this one', onSelect: () => addVisit(i) },
      {
        key: 'optional', label: OPTIONAL_LABEL, sub: OPTIONAL_SUB, checked: visit.optional,
        onSelect: () => {
          /*
           * A visit in some plans only is an answer, and an answer cannot also
           * be an extra (step 24 item 1). Said here, before the save, rather
           * than by the database the day the package goes on a quote.
           */
          if (!visit.optional && (draft.visits[i]?.planIds ?? []).length > 0) {
            setError(PLAN_VISIT_OPTIONAL)
            return
          }
          setError(null)
          patchVisit(i, { optional: !visit.optional })
        },
      },
      // A move that cannot happen, and the last visit's Remove, are absent rather than dead.
      ...(i > 0 ? [{ key: 'up', label: 'Move up', onSelect: () => moveVisit(i, i - 1) }] : []),
      ...(i < draft.visits.length - 1
        ? [{ key: 'down', label: 'Move down', onSelect: () => moveVisit(i, i + 1) }] : []),
      { key: 'collapse', label: collapsed.has(visit.id) ? 'Expand' : 'Collapse', onSelect: () => toggle(visit.id) },
      ...(draft.visits.length > 1
        ? [{
          key: 'remove', label: 'Remove', tone: 'danger' as const, separatorBefore: true,
          onSelect: () => removeVisit(i),
        }]
        : []),
    ]
  }

  /*
   * **The service's ⋮** (Aaron, review item 4) — the quote's, over the draft:
   * Duplicate · Add a service below · ✓ Optional · Move up · Move down ·
   * Collapse · Remove service. No Rename: a package's service carries the
   * catalog service's own name and keeps none of its own, which is why its
   * popover reads the name rather than taking one (`fromPackage`).
   */
  function serviceMenu(serviceId: string, from?: HTMLElement): MenuItem[] {
    if (!canEdit || !draft) return []
    const [v, s] = indexOf(serviceId)
    const service = draft.visits[v!]?.services[s!]
    if (!service) return []
    const count = draft.visits[v!]!.services.length
    const optional = service.optional ?? false
    return [
      { key: 'duplicate', label: VISIT_MENU.duplicate, onSelect: () => duplicateService(serviceId) },
      {
        key: 'below', label: ROW_MENU.addServiceBelow,
        onSelect: () => {
          if (from) anchor.current = from
          setEditing({ kind: 'addService', visitId: `v${v}`, afterServiceId: serviceId })
        },
      },
      {
        key: 'optional', label: OPTIONAL_LABEL, sub: OPTIONAL_SUB, checked: optional,
        onSelect: () => patchService(serviceId, { optional: !optional }),
      },
      ...(s! > 0 ? [{ key: 'up', label: VISIT_MENU.moveUp, onSelect: () => moveService(serviceId, s! - 1) }] : []),
      ...(s! < count - 1
        ? [{ key: 'down', label: VISIT_MENU.moveDown, onSelect: () => moveService(serviceId, s! + 1) }] : []),
      ...(phone ? [] : [{
        key: 'collapse',
        label: collapsed.has(serviceId) ? ROW_MENU.expand : VISIT_MENU.collapse,
        onSelect: () => toggle(serviceId),
      }]),
      {
        key: 'delete', label: ROW_MENU.removeService, tone: 'danger' as const, separatorBefore: true,
        onSelect: () => removeService(serviceId),
      },
    ]
  }

  /*
   * **The line's ⋮** (item 4) — the quote's: Duplicate · Add an item below ·
   * ✓ Optional · Move up · Move down · Reset to standard price · Remove line.
   * A copy keeps its rule and any price of its own; a package line has no
   * `hand_edited` to keep, because nothing re-scales a recipe but the recipe.
   */
  function lineMenu(lineId: string, edited: boolean, from?: HTMLElement): MenuItem[] {
    if (!canEdit || !draft) return []
    const [v, s, l] = indexOf(lineId)
    const line = draft.visits[v!]?.services[s!]?.lines[l!]
    if (!line) return []
    const count = draft.visits[v!]!.services[s!]!.lines.length
    const chip = line.kind === 'rental' ? 'equipment' : line.kind === 'sub' ? undefined : line.kind
    return [
      { key: 'duplicate', label: VISIT_MENU.duplicate, onSelect: () => duplicateLine(lineId) },
      {
        key: 'below', label: ROW_MENU.addItemBelow,
        onSelect: () => {
          anchor.current = from ?? document.querySelector(`[data-line-id="${lineId}"]`)
          setOpenService(`v${v}.s${s}`)
          setEditing({ kind: 'add', startKind: chip, afterLineId: lineId })
        },
      },
      {
        key: 'optional', label: OPTIONAL_LABEL, sub: OPTIONAL_SUB, checked: line.optional,
        onSelect: () => patchLine(lineId, { optional: !line.optional }),
      },
      ...(l! > 0 ? [{ key: 'up', label: VISIT_MENU.moveUp, onSelect: () => moveLine(lineId, l! - 1) }] : []),
      ...(l! < count - 1
        ? [{ key: 'down', label: VISIT_MENU.moveDown, onSelect: () => moveLine(lineId, l! + 1) }] : []),
      ...(edited
        ? [{ key: 'reset', label: ROW_MENU.resetPrice, onSelect: () => patchLine(lineId, { priceOverride: null }) }]
        : []),
      {
        key: 'delete', label: ROW_MENU.removeLine, tone: 'danger' as const, separatorBefore: true,
        onSelect: () => removeLine(lineId),
      },
    ]
  }

  /** The unit a line's rate is per: the package's measure, or its quantity unit. */
  const measureUnit = types.find((one) => one.id === draft?.measurementTypeId)?.unit ?? null
  const perOf = (scalesBy: ScalesBy) => (scalesBy === 'quantity' || draft?.asks === 'quantity'
    ? draft?.qtyUnit ?? ''
    : measureUnit ?? draft?.qtyUnit ?? '')
  /** The sample, as the rule line and the rail print it: "6,000". */
  const sampleValue = draft?.asks === 'quantity' ? sample.qty : sample.measure
  const sampleUnit = draft?.asks === 'quantity' ? draft.qtyUnit : measureUnit ?? ''
  const sampleText = formatNumber(sampleValue)

  /**
   * One line's figures as they would be with this draft of it — the Line
   * popover's live numbers, from the same preview as the page's (§7.3).
   */
  async function previewLine(key: string, over: PackageLineSpec) {
    if (!draft) return null
    const [v, s2, l] = indexOf(key)
    const changed: PackageDraft = {
      ...draft,
      visits: draft.visits.map((visit, at) => (at !== v ? visit : {
        ...visit,
        services: visit.services.map((service, one) => (one !== s2 ? service : {
          ...service, lines: service.lines.map((line, which) => (which === l ? over : line)),
        })),
      })),
    }
    const asked = previewable(changed)
    if (!asked) return null
    try {
      const answer = await request<PackagePreviewResponse>(`/catalog-admin/packages/${id}/preview`, {
        method: 'POST',
        body: { qty: sample.qty, measure: sample.measure, startsOn: today(), draft: asked.body },
      })
      return answer.visits[asked.sent.indexOf(v!)]?.services[s2!]?.lines[l!] ?? null
    } catch {
      return null
    }
  }

  const hasSubs = (tree?.visits ?? []).some((visit) => visit.services.some(
    (service) => service.lines.some((line) => line.kind === 'sub')))
  const railHidden = !railOn || narrow

  return {
    id, navigate, phone, narrow, draft, setDraft, divisions, types, units, error, setError, warnings,
    busy, preview, tree, sample, setSample, sampleValue, sampleUnit, sampleText, measureUnit, perOf,
    selected, setSelected, collapsed, toggle, allCollapsed, collapseAll, railOn, setRailOn, railHidden,
    menu, setMenu, editing, setEditing, marginScope, setMarginScope, summaryOpen, setSummaryOpen,
    openService, setOpenService, anchor, searchBox, packageBox, canEdit, openVisitId, openPhoneVisit,
    patch, patchVisit, patchService, patchLine, removeLine, removeService, setRule, addVisit,
    moveVisit, addService, add, addPackage, addPlan, editPlan, recommendPlan, removePlan, setMembership,
    addBillingPlan, editBillingPlan, removeBillingPlan, setVisitWhen, hasRental, removeVisit,
    save, duplicate, openVisit, openServiceEditor, openLine, openAdd, openMargin, fixCosts,
    visitMenu, serviceMenu, lineMenu, hasSubs, previewLine,
  }
}

